import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatError, WINDOWS } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Minus, Sparkles, ShieldCheck, ArrowRight, Flame, Users } from "lucide-react";

const TIPS = [0, 2, 6, 10];

const PICKUP_LOCATION_OPTIONS = [
  "Front door",
  "Outside garage door",
  "Designated valet trash spot",
  "Other",
];

const STATUS_STYLE = {
  open:         { label: "Plenty of room", cls: "bg-green-100 text-green-800 border-green-200", icon: null },
  filling:      { label: "Filling up",     cls: "bg-amber-100 text-amber-800 border-amber-200", icon: Users },
  almost_full:  { label: "Almost full",    cls: "bg-orange-100 text-orange-800 border-orange-200", icon: Users },
  full:         { label: "Full this week", cls: "bg-red-100 text-red-700 border-red-200", icon: null },
  started:      { label: "Started — closed", cls: "bg-slate-100 text-slate-500 border-slate-200", icon: null },
};

export default function Schedule() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [dateAvailability, setDateAvailability] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    community: "",
    residence_type: "apartment",
    building: "",
    unit: "",
    location_choice: "",
    location_other: "",
    location_instructions: "",
    bags: 1,
    window: "fri_am",
    pickup_date: "",
    order_type: "one_time",
    recurring_windows: [],
    monthly_cleaning: false,
    tip_amount: 0,
    custom_tip: "",
    bags_tied: false,
    bags_placed_outside: false,
    no_hazardous: false,
    outside_only_ack: false,
    referral_code: "",
    pay_in_person: false,
  });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/communities/active").then((r) => {
      const list = r.data || [];
      setCommunities(list);
      // Auto-select if there's only one approved community
      if (list.length === 1) {
        setForm((f) => f.community ? f : { ...f, community: list[0].name });
      }
    }).catch(() => {});
    api.get("/windows/availability").then((r) => {
      const data = r.data || [];
      setAvailability(data);
      // If the user's default-selected window is full, jump to the first open one
      setForm((f) => {
        const sel = data.find((x) => x.window === f.window);
        if (sel && sel.status === "full") {
          const open = data.find((x) => x.status !== "full");
          if (open) return { ...f, window: open.window };
        }
        return f;
      });
    }).catch(() => {});
    const ref = localStorage.getItem("bd_ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref }));
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
        phone: f.phone || user.phone || "",
      }));
    }
  }, [user]);

  // Refetch per-date availability whenever pickup_date changes so we can
  // disable windows that have already started for the chosen day.
  useEffect(() => {
    if (!form.pickup_date) {
      setDateAvailability([]);
      return;
    }
    api
      .get(`/windows/availability-for-date?date=${form.pickup_date}`)
      .then((r) => setDateAvailability(r.data || []))
      .catch(() => setDateAvailability([]));
  }, [form.pickup_date]);

  // Keep location_instructions in sync with the dropdown choice + "Other" text.
  useEffect(() => {
    if (!form.location_choice) return;
    const v =
      form.location_choice === "Other"
        ? (form.location_other || "").trim()
        : form.location_choice;
    setForm((f) => (f.location_instructions === v ? f : { ...f, location_instructions: v }));
  }, [form.location_choice, form.location_other]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const computePrice = () => {
    if (!settings) return { base: 0, cleaning: 0, tip: 0, total: 0 };
    const b = Math.max(1, parseInt(form.bags || 1));
    let base = 0;
    if (b === 1) base = settings.price_1_bag;
    else if (b === 2) base = settings.price_2_bags;
    else base = settings.price_2_bags + (b - 2) * settings.price_extra_bag;
    const cleaning = form.monthly_cleaning ? settings.monthly_cleaning_price : 0;
    const tip =
      form.tip_amount === "custom"
        ? parseFloat(form.custom_tip || 0) || 0
        : parseFloat(form.tip_amount || 0) || 0;
    return {
      base: round(base),
      cleaning: round(cleaning),
      tip: round(tip),
      total: round(base + cleaning + tip),
    };
  };

  const pricing = computePrice();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please log in or create an account to schedule.");
      navigate("/login?next=/schedule");
      return;
    }
    if (!form.community) {
      toast.error("Please pick an approved community.");
      return;
    }
    if (form.residence_type === "townhome" && !form.location_choice) {
      toast.error("Please choose where the kids should pick up the bag.");
      return;
    }
    if (form.location_choice === "Other" && !(form.location_other || "").trim()) {
      toast.error("Please describe where the kids should pick up the bag.");
      return;
    }
    if (!(form.bags_tied && form.bags_placed_outside && form.no_hazardous && form.outside_only_ack)) {
      toast.error("Please confirm all safety checkboxes.");
      return;
    }
    if (form.order_type === "one_time" && !form.pickup_date) {
      toast.error("Please pick a pickup date.");
      return;
    }
    if (form.order_type === "recurring" && form.recurring_windows.length === 0) {
      toast.error("Pick at least one weekly window for recurring service.");
      return;
    }
    if (form.order_type === "recurring" && form.bags > 2) {
      toast.error("Weekly subscription is for 1 or 2 bags. Reduce to 1 or 2 bags, or switch to One-time.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        bags: parseInt(form.bags),
        tip_amount: pricing.tip,
        recurring_windows: form.order_type === "recurring" ? form.recurring_windows : null,
        pickup_date: form.order_type === "one_time" ? form.pickup_date : null,
        referral_code: form.referral_code || null,
      };
      delete payload.custom_tip;
      const { data } = await api.post("/orders", payload);
      if (form.pay_in_person) {
        toast.success("Order saved. Pay your kid worker on pickup day!");
        navigate(`/dashboard`);
      } else {
        toast.success("Order created. Redirecting to checkout…");
        navigate(`/checkout/${data.id}`);
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="mb-6">
        <h1 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900" data-testid="schedule-title">Schedule a Pickup</h1>
        <p className="text-slate-600 mt-1">Tell us where to dash. We'll handle the rest.</p>
      </div>

      {settings && !settings.booking_enabled && (
        <div className="card-clay p-5 mb-6 border-amber-200 bg-amber-50" data-testid="booking-closed">
          <div className="font-fredoka font-bold text-amber-900">Booking is paused</div>
          <p className="text-sm text-amber-800 mt-1">We're temporarily not accepting new pickups. Check back soon!</p>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-6">
        <Section title="Your contact info">
          <Grid2>
            <Field label="Full name" required>
              <input className="input-clay h-11 px-3 w-full" value={form.name} onChange={(e) => upd("name", e.target.value)} required data-testid="field-name" />
            </Field>
            <Field label="Phone" required>
              <input className="input-clay h-11 px-3 w-full" value={form.phone} onChange={(e) => upd("phone", e.target.value)} required data-testid="field-phone" />
            </Field>
            <Field label="Email" required>
              <input type="email" className="input-clay h-11 px-3 w-full" value={form.email} onChange={(e) => upd("email", e.target.value)} required data-testid="field-email" />
            </Field>
          </Grid2>
        </Section>

        <Section title="Pickup location">
          {communities.length === 0 && (
            <div className="p-3 rounded-xl bg-amber-50 border-2 border-amber-200 text-amber-900 text-sm mb-3" data-testid="no-communities-warning">
              Bag Dash Kids is currently serving select communities only. Please check back soon or request your community to be added.
            </div>
          )}
          <Field label="Community" required>
            <select
              className="input-clay h-11 px-3 w-full bg-white"
              value={form.community}
              onChange={(e) => {
                const v = e.target.value;
                upd("community", v);
                // Reset building when community changes
                upd("building", "");
              }}
              required
              data-testid="field-community"
            >
              <option value="">Select community…</option>
              {communities.map((c) => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </Field>

          {form.community && (() => {
            const selected = communities.find((c) => c.name === form.community);
            if (!selected) return null;
            return (
              <>
                <div className="mt-4">
                  <div className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2">Residence type</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => { upd("residence_type", "apartment"); upd("building", ""); }}
                      className={`rounded-xl px-3 py-3 border-2 text-sm font-semibold transition ${form.residence_type === "apartment" ? "bg-green-500 text-white border-green-700" : "bg-white border-slate-200 text-slate-700 hover:border-green-400"}`}
                      data-testid="residence-apartment"
                    >
                      🏢 Apartment
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!selected.allows_townhomes) {
                          toast.error("Townhome pickup isn't available for this community yet.");
                          return;
                        }
                        upd("residence_type", "townhome");
                        upd("building", "");
                      }}
                      disabled={!selected.allows_townhomes}
                      className={`rounded-xl px-3 py-3 border-2 text-sm font-semibold transition ${form.residence_type === "townhome" ? "bg-green-500 text-white border-green-700" : selected.allows_townhomes ? "bg-white border-slate-200 text-slate-700 hover:border-green-400" : "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"}`}
                      data-testid="residence-townhome"
                    >
                      🏘️ Townhome
                    </button>
                  </div>
                </div>

                {form.residence_type === "apartment" ? (
                  <Grid2 className="mt-4">
                    <Field label="Building #" required>
                      <select
                        className="input-clay h-11 px-3 w-full bg-white"
                        value={form.building}
                        onChange={(e) => upd("building", e.target.value)}
                        required
                        data-testid="field-building"
                      >
                        <option value="">Select your building…</option>
                        {(selected.buildings || []).map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Unit #" required>
                      <input className="input-clay h-11 px-3 w-full" value={form.unit} onChange={(e) => upd("unit", e.target.value)} required data-testid="field-unit" />
                    </Field>
                    <PickupLocationField form={form} upd={upd} required={false} />
                  </Grid2>
                ) : (
                  <Grid2 className="mt-4">
                    <Field label="Townhome address / unit identifier" required>
                      <input
                        className="input-clay h-11 px-3 w-full"
                        placeholder="e.g. 9100 Whitehall Blvd, Unit C or Townhome #14"
                        value={form.building}
                        onChange={(e) => upd("building", e.target.value)}
                        required
                        data-testid="field-townhome-address"
                      />
                    </Field>
                    <PickupLocationField form={form} upd={upd} required={true} />
                  </Grid2>
                )}
              </>
            );
          })()}
        </Section>

        <Section title="Service">
          <div className="grid sm:grid-cols-2 gap-3">
            <button type="button" onClick={() => upd("order_type", "one_time")} className={`rounded-2xl p-4 border-2 text-left transition-all ${form.order_type === "one_time" ? "bg-green-50 border-green-500" : "bg-white border-slate-200 hover:border-slate-300"}`} data-testid="type-onetime">
              <div className="font-fredoka font-bold text-slate-900">One-time pickup</div>
              <div className="text-sm text-slate-600 mt-1">Just this once. Pay per pickup.</div>
            </button>
            <button type="button" onClick={() => upd("order_type", "recurring")} className={`rounded-2xl p-4 border-2 text-left transition-all ${form.order_type === "recurring" ? "bg-green-50 border-green-500" : "bg-white border-slate-200 hover:border-slate-300"}`} data-testid="type-recurring">
              <div className="font-fredoka font-bold text-slate-900">Weekly subscription</div>
              <div className="text-sm text-slate-600 mt-1">Auto-billed by Stripe every week. Cancel anytime. 1 bag = $3/wk · 2 bags = $5/wk.</div>
            </button>
          </div>

          {form.order_type === "recurring" && form.bags > 2 && (
            <div className="card-clay p-3 mt-3 bg-amber-50 border-amber-200 text-amber-900 text-sm" data-testid="recurring-bag-limit-warning">
              Weekly subscription is available for <b>1 or 2 bags</b>. Bring it down to 1 or 2 bags, or use One-time pickup if you regularly have more.
            </div>
          )}

          {form.order_type === "one_time" && (
            <div className="mt-4">
              <div className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2">Pickup date</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="date-picker-grid">
                {generateFutureFriSat(8, settings?.blackout_dates || []).map((d) => {
                  const selected = form.pickup_date === d.iso;
                  return (
                    <button
                      type="button"
                      key={d.iso}
                      onClick={() => {
                        upd("pickup_date", d.iso);
                        // Auto-align window to the chosen day if needed
                        const isFri = d.weekday === 5; // JS Fri=5
                        if (isFri && !form.window.startsWith("fri")) upd("window", "fri_am");
                        if (!isFri && !form.window.startsWith("sat")) upd("window", "sat_am");
                      }}
                      className={`rounded-xl p-2 border-2 text-center transition ${selected ? "bg-green-500 text-white border-green-700" : "bg-white border-slate-200 text-slate-700 hover:border-green-400"}`}
                      data-testid={`pickup-date-${d.iso}`}
                    >
                      <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{d.weekdayShort}</div>
                      <div className="font-fredoka text-lg font-bold">{d.monthDay}</div>
                    </button>
                  );
                })}
              </div>
              <div className="text-xs text-slate-500 mt-2">Only Fridays and Saturdays — those are our service days.</div>
            </div>
          )}

          {form.order_type === "one_time" && (
            <div className="mt-4">
              <div className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2">Pickup window</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {WINDOWS.map((w) => {
                  const a = availability.find((x) => x.window === w.value);
                  // If a pickup_date is selected, prefer the date-specific
                  // availability (so today's already-started windows lock).
                  const da = dateAvailability.find((x) => x.window === w.value);
                  const dayMismatch = da?.status === "wrong_day";
                  const started = da?.started || a?.status === "started";
                  const dayFull = da?.status === "full";
                  const weekFull = !dayFull && a?.status === "full";
                  const isFull = dayMismatch || started || dayFull || weekFull;
                  return (
                    <button
                      type="button"
                      key={w.value}
                      onClick={() => !isFull && upd("window", w.value)}
                      disabled={isFull}
                      className={`rounded-xl px-3 py-3 border-2 text-sm font-semibold relative flex flex-col items-stretch gap-1.5 transition ${
                        form.window === w.value ? "bg-green-500 text-white border-green-700"
                          : isFull ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                          : "bg-white border-slate-200 text-slate-700 hover:border-green-400"
                      }`}
                      data-testid={`window-${w.value}`}
                    >
                      {a?.is_most_popular && !isFull && (
                        <span className="absolute -top-2 -right-2 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-orange-500 text-white border border-orange-600 shadow-sm" data-testid={`window-${w.value}-popular`}>
                          <Flame size={9} strokeWidth={3}/> Popular
                        </span>
                      )}
                      <span>{w.label}</span>
                      {a?.time_label && <span className="text-[10px] opacity-75 font-normal">{a.time_label}</span>}
                      {dayMismatch ? (
                        <span className="inline-flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200" data-testid={`window-${w.value}-status`}>
                          Wrong day
                        </span>
                      ) : started ? (
                        <span className="inline-flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200" data-testid={`window-${w.value}-status`}>
                          Started — closed
                        </span>
                      ) : a ? (
                        <WindowHint a={a} active={form.window === w.value}/>
                      ) : null}
                    </button>
                  );
                })}
              </div>
              {form.pickup_date && dateAvailability.every((x) => x.status !== "open" && x.status !== "filling" && x.status !== "almost_full") && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="all-windows-closed">
                  All windows are closed for the selected date. Please pick another Friday or Saturday.
                </div>
              )}
            </div>
          )}

          {form.order_type === "recurring" && (
            <div className="mt-4">
              <div className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2">Choose one or more windows</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {WINDOWS.map((w) => {
                  const active = form.recurring_windows.includes(w.value);
                  const a = availability.find((x) => x.window === w.value);
                  const isFull = a?.status === "full" || a?.status === "started";
                  return (
                    <button type="button" key={w.value} disabled={isFull} onClick={() => {
                      if (isFull) return;
                      const arr = active ? form.recurring_windows.filter((v) => v !== w.value) : [...form.recurring_windows, w.value];
                      upd("recurring_windows", arr);
                      if (arr.length && !active) upd("window", w.value);
                    }} className={`rounded-xl px-3 py-3 border-2 text-sm font-semibold relative flex flex-col items-stretch gap-1.5 transition ${
                      active ? "bg-green-500 text-white border-green-700"
                        : isFull ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
                        : "bg-white border-slate-200 text-slate-700 hover:border-green-400"
                    }`} data-testid={`rwindow-${w.value}`}>
                      {a?.is_most_popular && (
                        <span className="absolute -top-2 -right-2 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-orange-500 text-white border border-orange-600 shadow-sm">
                          <Flame size={9} strokeWidth={3}/> Popular
                        </span>
                      )}
                      <span>{w.label}</span>
                      {a && <WindowHint a={a} active={active}/>}
                    </button>
                  );
                })}
              </div>
              <div className="text-xs text-slate-500 mt-2">First pickup will use the earliest selected window.</div>
            </div>
          )}

          <div className="mt-5">
            <div className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2">Number of bags</div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => upd("bags", Math.max(1, parseInt(form.bags) - 1))} className="w-11 h-11 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center border-2 border-slate-200" data-testid="bags-decrement"><Minus size={18}/></button>
              <div className="font-fredoka text-3xl font-bold w-12 text-center" data-testid="bags-count">{form.bags}</div>
              <button type="button" onClick={() => upd("bags", Math.min(20, parseInt(form.bags) + 1))} className="w-11 h-11 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center border-2 border-slate-200" data-testid="bags-increment"><Plus size={18}/></button>
              <div className="ml-3 text-sm text-slate-600">
                {settings && (
                  <>Base: <span className="font-bold text-slate-900">${pricing.base.toFixed(2)}</span></>
                )}
              </div>
            </div>
            <div className="text-xs text-slate-500 mt-2">$3 for 1 bag · $5 for 2 bags · +$2 per extra bag</div>
          </div>

          <label className="mt-5 flex items-center justify-between gap-3 rounded-2xl border-2 border-amber-100 bg-amber-50 p-4 cursor-pointer" data-testid="monthly-cleaning-toggle">
            <div className="flex items-center gap-3">
              <Sparkles className="text-amber-600" strokeWidth={2.5}/>
              <div>
                <div className="font-fredoka font-bold text-slate-900">Monthly bin cleaning</div>
                <div className="text-xs text-amber-800">Loyal-customer favorite · ${settings?.monthly_cleaning_price?.toFixed(2) || "10.00"}</div>
              </div>
            </div>
            <input type="checkbox" className="w-5 h-5 accent-amber-500" checked={form.monthly_cleaning} onChange={(e) => upd("monthly_cleaning", e.target.checked)} />
          </label>
        </Section>

        <Section title="Add a tip (optional)">
          <div className="flex flex-wrap gap-2">
            {TIPS.map((t) => (
              <button type="button" key={t} onClick={() => upd("tip_amount", t)} className={`rounded-xl px-4 py-2 border-2 text-sm font-semibold ${form.tip_amount === t ? "bg-amber-500 text-white border-amber-700" : "bg-white border-slate-200 text-slate-700"}`} data-testid={`tip-${t}`}>
                {t === 0 ? "No tip" : `$${t}`}
              </button>
            ))}
            <button type="button" onClick={() => upd("tip_amount", "custom")} className={`rounded-xl px-4 py-2 border-2 text-sm font-semibold ${form.tip_amount === "custom" ? "bg-amber-500 text-white border-amber-700" : "bg-white border-slate-200 text-slate-700"}`} data-testid="tip-custom">Custom</button>
            {form.tip_amount === "custom" && (
              <input type="number" min="0" step="0.5" className="input-clay h-10 px-3 w-28" value={form.custom_tip} onChange={(e) => upd("custom_tip", e.target.value)} placeholder="$" data-testid="tip-custom-input" />
            )}
          </div>
        </Section>

        <Section title="Safety promise (required)">
          <div className="grid sm:grid-cols-2 gap-2">
            <Check label="Bags will be tied securely." value={form.bags_tied} onChange={(v) => upd("bags_tied", v)} testid="ack-tied" />
            <Check label="Bags placed outside at agreed location." value={form.bags_placed_outside} onChange={(v) => upd("bags_placed_outside", v)} testid="ack-outside" />
            <Check label="No hazardous, sharp, leaking, or oversized items." value={form.no_hazardous} onChange={(v) => upd("no_hazardous", v)} testid="ack-hazardous" />
            <Check label="Pickup is outside only — no entry into homes." value={form.outside_only_ack} onChange={(v) => upd("outside_only_ack", v)} testid="ack-outsideonly" />
          </div>
        </Section>

        <Section title="Referral code (optional)">
          <input className="input-clay h-11 px-3 w-full sm:w-72" placeholder="DASH123ABC" value={form.referral_code} onChange={(e) => upd("referral_code", e.target.value.toUpperCase())} data-testid="field-referral" />
          <p className="text-xs text-slate-500 mt-1">Got a friend's code? They earn a bonus point after admin approval.</p>
        </Section>

        <Section title="How would you like to pay?">
          <div className="grid sm:grid-cols-2 gap-3">
            <button type="button" onClick={() => upd("pay_in_person", false)} className={`rounded-2xl p-4 border-2 text-left transition-all ${!form.pay_in_person ? "bg-green-50 border-green-500" : "bg-white border-slate-200 hover:border-slate-300"}`} data-testid="pay-stripe">
              <div className="font-fredoka font-bold text-slate-900">💳 Pay online</div>
              <div className="text-sm text-slate-600 mt-1">Stripe Checkout · Apple Pay & Google Pay supported.</div>
            </button>
            <button type="button" onClick={() => upd("pay_in_person", true)} className={`rounded-2xl p-4 border-2 text-left transition-all ${form.pay_in_person ? "bg-green-50 border-green-500" : "bg-white border-slate-200 hover:border-slate-300"}`} data-testid="pay-in-person">
              <div className="font-fredoka font-bold text-slate-900">🤝 Pay in person</div>
              <div className="text-sm text-slate-600 mt-1">Cash or check at pickup. Admin marks paid after.</div>
            </button>
          </div>
        </Section>

        {/* Summary + submit */}
        <div className="card-clay p-6 sticky bottom-3">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Estimated total</div>
              <div className="font-fredoka text-4xl font-bold text-slate-900" data-testid="total-amount">${pricing.total.toFixed(2)}</div>
              <div className="text-xs text-slate-500 mt-1">
                Base ${pricing.base.toFixed(2)}{pricing.cleaning ? ` · Cleaning $${pricing.cleaning.toFixed(2)}` : ""}{pricing.tip ? ` · Tip $${pricing.tip.toFixed(2)}` : ""}
              </div>
            </div>
            <button type="submit" disabled={submitting} className="btn-push-green px-6 py-3 inline-flex items-center gap-2" data-testid="submit-order-btn">
              {submitting ? "Saving…" : (form.pay_in_person ? "Confirm pickup" : "Continue to checkout")} <ArrowRight size={18} strokeWidth={2.5}/>
            </button>
          </div>
          {!user && (
            <div className="mt-3 text-sm text-slate-600 flex items-center gap-2">
              <ShieldCheck className="text-green-600" size={16} strokeWidth={2.5}/>
              <span>You'll need to <Link to="/login" className="text-green-700 font-bold underline">log in</Link> or <Link to="/register" className="text-green-700 font-bold underline">create an account</Link> before payment.</span>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

const round = (n) => Math.round(n * 100) / 100;

function WindowHint({ a, active }) {
  const s = STATUS_STYLE[a.status] || STATUS_STYLE.open;
  // When the button is the selected (green) one, use lighter on-green chip
  const cls = active
    ? "bg-white/20 text-white border border-white/30"
    : `${s.cls} border`;
  const Icon = s.icon;
  return (
    <span
      className={`inline-flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${cls}`}
      title={`${a.bookings} of ${a.capacity} booked for ${a.pickup_date}`}
      data-testid={`window-${a.window}-status`}
    >
      {Icon ? <Icon size={9} strokeWidth={3}/> : null}
      {s.label}
    </span>
  );
}

const Section = ({ title, children }) => (
  <div className="card-clay p-5 sm:p-6">
    <h2 className="font-fredoka text-xl font-bold text-slate-900 mb-4">{title}</h2>
    {children}
  </div>
);

const Field = ({ label, required, children }) => (
  <label className="block">
    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </div>
    {children}
  </label>
);

const Grid2 = ({ children, className = "" }) => <div className={`grid sm:grid-cols-2 gap-3 ${className}`}>{children}</div>;

// Returns the next N Fridays + Saturdays (interleaved chronologically), skipping any blackout dates.
// Output: [{ iso: "2026-06-26", weekday: 5, weekdayShort: "Fri", monthDay: "Jun 26" }, ...]
function generateFutureFriSat(weeksAhead = 8, blackoutDates = []) {
  const out = [];
  const blackout = new Set(blackoutDates);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i < weeksAhead * 7 + 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const dow = d.getDay(); // 0=Sun .. 5=Fri, 6=Sat
    if (dow !== 5 && dow !== 6) continue;
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (blackout.has(iso)) continue;
    out.push({
      iso,
      weekday: dow,
      weekdayShort: dow === 5 ? "Fri" : "Sat",
      monthDay: d.toLocaleString("en-US", { month: "short", day: "numeric" }),
    });
    if (out.length >= weeksAhead * 2) break;
  }
  return out;
}

const Check = ({ label, value, onChange, testid }) => (
  <label className={`flex items-center gap-3 rounded-2xl border-2 p-3 cursor-pointer transition-all ${value ? "bg-green-50 border-green-500" : "bg-white border-slate-200 hover:border-slate-300"}`} data-testid={testid}>
    <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="w-5 h-5 accent-green-500" />
    <span className="text-sm font-semibold text-slate-700">{label}</span>
  </label>
);

function PickupLocationField({ form, upd, required }) {
  return (
    <div className="sm:col-span-2 space-y-2">
      <Field label="Where should we pick up the bag?" required={required}>
        <select
          className="input-clay h-11 px-3 w-full bg-white"
          value={form.location_choice}
          onChange={(e) => upd("location_choice", e.target.value)}
          required={required}
          data-testid="field-location-choice"
        >
          <option value="">Select a spot…</option>
          {PICKUP_LOCATION_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </Field>
      {form.location_choice === "Other" && (
        <Field label="Describe the spot" required={required}>
          <input
            className="input-clay h-11 px-3 w-full"
            placeholder="e.g. front porch under the bench, by the side gate"
            value={form.location_other}
            onChange={(e) => upd("location_other", e.target.value)}
            required={required}
            data-testid="field-location-other"
          />
        </Field>
      )}
    </div>
  );
}
